import { RefObject } from 'react'
import { FormInstance } from 'rsuite'

import { getRefDimension } from './util'

export const animateScroll = (
  initialPosition: number,
  targetPosition: number,
  duration = 1000,
  offset = 0,
) => {
  // Maximum amount of pixels user can scroll
  const maxAvailableScroll = document.documentElement.scrollHeight
    - document.documentElement.clientHeight
  const targetScrollDistance = initialPosition - targetPosition + offset

  // Decelerate scroll animation
  const easeOutAnimation = (animationProgress: number) => 1 - ((1 - animationProgress) ** 4)

  let startTimestamp: number
  let animationFrame: number

  const scrollIncrement = (timestamp: number) => {
    if (!startTimestamp) { startTimestamp = timestamp }

    const elapsed = timestamp - startTimestamp

    // Returns a number between 0 (startTimestamp) and 1 (end)
    const relativeProgress = elapsed / duration

    // Ease out relative progress
    const easedProgress = easeOutAnimation(relativeProgress)

    // Calculate new position for current animation frame
    const position = initialPosition - targetScrollDistance * Math.min(easedProgress, 1)

    // Set scrollbar position
    window.scrollTo(0, position)

    // Stop if max scroll is reached
    if (
      initialPosition !== maxAvailableScroll
      && window.scrollY === maxAvailableScroll
    ) {
      window.cancelAnimationFrame(animationFrame)
      return
    }

    // Repeat until the end is reached
    if (elapsed < duration) {
      animationFrame = window.requestAnimationFrame(scrollIncrement)
    }
  }

  animationFrame = window.requestAnimationFrame(scrollIncrement)
}

export const scrollToRef = (
  ref?: RefObject<HTMLElement>,
  offset = 0,
  duration = 1000,
) => {
  const currentScrollPosition = window.scrollY
  // Ref top in relation to document
  const refTopPosition = getRefDimension('top', ref) + currentScrollPosition

  animateScroll(
    currentScrollPosition,
    refTopPosition,
    duration,
    offset,
  )
}

export const isElementVisible = (element: HTMLElement | null | undefined) => {
  if (!element) {
    return false
  }

  const rect = element.getBoundingClientRect()

  // Check if the element is within the viewport
  const isInViewport = (
    rect.top >= 0
    && rect.left >= 0
    && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
    && rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  )

  if (!isInViewport) {
    return false
  }

  // Check if the element is obscured
  // Targets element center
  const elementFromPoint = document.elementFromPoint(
    rect.left + rect.width / 2,
    rect.top + rect.height / 2,
  )

  return element.contains(elementFromPoint)
}

export const scrollToFirstError = (
  formRef: RefObject<FormInstance>,
  offset = 180,
  duration = 400,
) => {
  if (!formRef.current) return

  // Add a small delay to allow error messages to render
  setTimeout(() => {
    const formElement = formRef.current?.root

    if (!formElement) return

    const firstErrorField = formElement.querySelector('[aria-invalid="true"]')

    if (!firstErrorField) return

    const formGroup = firstErrorField.closest('.rs-form-group')
    const targetElement = formGroup || firstErrorField

    const currentScrollPosition = window.scrollY
    const fieldTopPosition = targetElement.getBoundingClientRect().top + currentScrollPosition

    animateScroll(
      currentScrollPosition,
      fieldTopPosition,
      duration,
      offset,
    )
  }, 100) // Small delay to ensure DOM updates are complete
}
